import type { IFolder } from "@/types/folder";
import type { FormikHelpers } from "formik";

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  CreateFolderModalLazy,
  CreateSubfolderModalLazy,
} from "@/components/folderModals";
import RenderOnce from "@/components/ui/renderOnce";
import { FOLDER_NOT_UNIQUE_NAME_ERROR_KEY } from "@/constants/folders";
import { CREATE_FOLDER_FIELDS } from "@/constants/formFields/createFolderModalFields";
import { CREATE_SUBFOLDER_FIELDS } from "@/constants/formFields/createSubfolderModalFields";
import { ROUTES } from "@/constants/routes";
import { currentFolderVar } from "@/graphql/globalVar/state";
import useCreateFolder from "@/hooks/folders/useCreateFolder";
import useCreateSubfolder from "@/hooks/folders/useCreateSubfolder";
import useGetUserFolders from "@/hooks/folders/useGetUserFolders";
import useLocalizedHref from "@/hooks/useLocalizedHref";
import { useAuthService } from "@/services/authService";
import { graphqlErrorService } from "@/services/graphqlErrorService";
import { notificationService } from "@/services/notificationService";
import { findFolderById } from "@/utils/findFolderById";
import { canEditFolder } from "@/utils/folderAccess";
import { isSameNormalizedFolderName } from "@/utils/normalizeFolderName";

function hasFolderWithName(folders: IFolder[], name: string) {
  return folders.some((folder) =>
    isSameNormalizedFolderName(folder.name, name)
  );
}

function useFolders() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const localizedHref = useLocalizedHref();
  const navigate = useNavigate();
  const { id: currentUserId } = useAuthService();

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showCreateSubfolderModal, setShowCreateSubfolderModal] =
    useState(false);

  const {
    data: userFolders,
    refetch: refetchFolders,
    loading: loadingFolders,
    error: foldersError,
  } = useGetUserFolders();

  const currentFolder = useMemo(() => {
    if (!searchParams.has("folder")) return null;

    const folderId = Number(searchParams.get("folder"));
    return findFolderById(userFolders, folderId);
  }, [searchParams, userFolders]);
  const canCreateFolderInCurrentContext = currentFolder
    ? canEditFolder(currentFolder, currentUserId, userFolders)
    : true;

  const onOpenFolder = useCallback(
    (folder: IFolder) => {
      currentFolderVar(folder);
      navigate(localizedHref(ROUTES.ENTRY));
      const params = new URLSearchParams(searchParams);
      params.set("folder", String(folder.id));
      params.set("page", String(1));
      params.delete("types");
      setSearchParams(params);
    },
    [localizedHref, navigate, searchParams, setSearchParams]
  );

  const openAllQrCodes = useCallback(() => {
    navigate(localizedHref(ROUTES.ENTRY));
    currentFolderVar(null);
  }, [localizedHref, navigate]);

  const { createFolder, loading: createFolderLoading } = useCreateFolder({
    onCompleted: () => {
      setShowCreateFolderModal(false);
      refetchFolders();
    },
  });

  const { createSubfolder, loading: createSubfolderLoading } =
    useCreateSubfolder({
      onCompleted: () => {
        setShowCreateSubfolderModal(false);
        refetchFolders();
      },
    });

  const onCreateFolder = async (
    values: { [CREATE_FOLDER_FIELDS.NAME]: string },
    { setErrors }: FormikHelpers<{ [CREATE_FOLDER_FIELDS.NAME]: string }>
  ) => {
    const name = values[CREATE_FOLDER_FIELDS.NAME];
    const duplicatedError = t(`errors:${FOLDER_NOT_UNIQUE_NAME_ERROR_KEY}`);

    if (hasFolderWithName(userFolders, name)) {
      setErrors({ [CREATE_FOLDER_FIELDS.NAME]: duplicatedError });

      return;
    }

    try {
      await createFolder({ name });
    } catch (error) {
      const { errorFields, errorKey, errorTranslation } =
        graphqlErrorService(error);
      if (errorKey === FOLDER_NOT_UNIQUE_NAME_ERROR_KEY) {
        setErrors({
          [CREATE_FOLDER_FIELDS.NAME]: duplicatedError,
        });

        return;
      }

      if (Object.keys(errorFields).length) {
        setErrors(errorFields);
      } else {
        notificationService.danger({ msg: errorTranslation });
      }
    }
  };

  const onCreateSubfolder = async (
    values: { [CREATE_SUBFOLDER_FIELDS.NAME]: string },
    { setErrors }: FormikHelpers<{ [CREATE_SUBFOLDER_FIELDS.NAME]: string }>
  ) => {
    if (!currentFolder || !canCreateFolderInCurrentContext) return;

    const name = values[CREATE_SUBFOLDER_FIELDS.NAME];
    const duplicatedError = t(`errors:${FOLDER_NOT_UNIQUE_NAME_ERROR_KEY}`);

    if (hasFolderWithName(currentFolder.subfolders ?? [], name)) {
      setErrors({ [CREATE_SUBFOLDER_FIELDS.NAME]: duplicatedError });

      return;
    }

    try {
      await createSubfolder({
        parentId: currentFolder.id,
        name,
      });
    } catch (error) {
      const { errorFields, errorKey, errorTranslation } =
        graphqlErrorService(error);
      if (errorKey === FOLDER_NOT_UNIQUE_NAME_ERROR_KEY) {
        setErrors({
          [CREATE_SUBFOLDER_FIELDS.NAME]: duplicatedError,
        });

        return;
      }

      if (Object.keys(errorFields).length) {
        setErrors(errorFields);
      } else {
        notificationService.danger({ msg: errorTranslation });
      }
    }
  };

  const openCreateModal = useCallback(() => {
    if (currentFolder) {
      if (!canCreateFolderInCurrentContext) return;

      setShowCreateSubfolderModal(true);
    } else {
      setShowCreateFolderModal(true);
    }
  }, [canCreateFolderInCurrentContext, currentFolder]);

  const modals = (
    <>
      <RenderOnce when={showCreateFolderModal}>
        <CreateFolderModalLazy
          show={showCreateFolderModal}
          handleClose={() => setShowCreateFolderModal(false)}
          onAction={onCreateFolder}
          isActionLoading={createFolderLoading}
        />
      </RenderOnce>
      <RenderOnce when={showCreateSubfolderModal && !!currentFolder}>
        <CreateSubfolderModalLazy
          show={showCreateSubfolderModal}
          handleClose={() => setShowCreateSubfolderModal(false)}
          onAction={onCreateSubfolder}
          isActionLoading={createSubfolderLoading}
        />
      </RenderOnce>
    </>
  );

  return {
    userFolders,
    currentFolder,
    refetchFolders,
    loadingFolders,
    foldersError,
    onOpenFolder,
    openAllQrCodes,
    openCreateModal,
    canCreateFolderInCurrentContext,
    setShowCreateFolderModal,
    modals,
  };
}

export default useFolders;
